import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { showErrorToast, showSuccessToast } from "../../../lib/toast";

import { getProducts, isCreditAccountStakeholdersRevampEnabled } from "../../configs/logic/Slice";
import { updateCase } from "../../../thunks/Case";
import { addCreditAccounts } from "../logic/thunks";
import { fetchEnrichedStakeholder } from "../../stakeholder/logic/stakeholderThunks";

import { getAccountProductInfo } from "../logic/slice";
import { selectLoanProductInfo } from "../../caseinfo/logic/Slice";
import {
  FullStakeholderData,
  getFullStakeholders,
} from "../../stakeholder/logic/stakeholderSlice";

import type { Stakeholder } from "../../../api/stakeholders";
import { CREDIT_FACILITY_LABELS, type CreateCreditAccountData, type CreditAccountProduct, type CreditFacility } from "../../../api/credit_accounts";

import { AddEntitiesModal } from "../../workbench/components/AddEntitiesModal";
import AddLoanAccountButton from "./AddLoanAccountButton";
import AccountRow, { CreateCreditAccountDataWithId } from "./AccountRow";
import { StakeholderOption } from "./AccountNameSearchInput";
import { v4 as uuidv4 } from "uuid";
import { EntityType, RelatedAs, StakeholderRelationship } from "../../../api/stakeholder_relationships";

interface AddAccountModalProps {
  onClose: () => void;
  onBack?: () => void;
  prefillExisting?: boolean;
  title?: string;
}

const CreditAccountsModal: React.FC<AddAccountModalProps> = ({
  onClose, 
  onBack, 
  prefillExisting,
  title="Add Loan Accounts"
}) => {
  const dispatch = useAppDispatch();
  const { id: caseId } = useParams<{ id: string }>();

  const existingStakeholders = useAppSelector(getFullStakeholders);
  const [newStakeholders, setNewStakaholders] = useState<FullStakeholderData[]>([]);

  
  
  const isRevampEnabled = useAppSelector(isCreditAccountStakeholdersRevampEnabled);
  useEffect(() => {
    if (!caseId || !isRevampEnabled || existingStakeholders.length === 0) return;
    const stakeholderIds = existingStakeholders.map((s) => s.stakeholder.stakeholder_id);
    dispatch(
      fetchEnrichedStakeholder({
        caseId,
        stakeholderIds,
      })
    );
  }, [caseId, isRevampEnabled, existingStakeholders, dispatch]);


  const loanReferences =useAppSelector(selectLoanProductInfo);
  const accountInfo = useAppSelector(getAccountProductInfo);

  const existingAccounts: CreateCreditAccountDataWithId[] = accountInfo.map((account) => ({
        account_number: account.loan_reference_id,
        ext_account_number: account.ext_account_number,
        product: account.product as CreditAccountProduct,
        credit_facility: account.credit_facility? account.credit_facility as CreditFacility: undefined,
        account_name: account.account_name,
      }))

  const initialRows = (): CreateCreditAccountDataWithId[] =>
    prefillExisting ? [] : [{ account_number: uuidv4() }];

  const [newCreditAccounts, setNewCreditAccounts] = useState<CreateCreditAccountDataWithId[]>(initialRows);

  const stakeholderOptions = useMemo<StakeholderOption[]>(() => {
    const existingOptions = existingStakeholders.map((stakeholder) => ({...stakeholder, isNew: false}));
    const newOptions = newStakeholders
      .filter((stakeholder) => newCreditAccounts.some((a) => a.primary_borrower_stakeholder_id === stakeholder.stakeholder.stakeholder_id))
      .map((stakeholder) => {
        const relatedAccounts = newCreditAccounts.filter((a) => a.primary_borrower_stakeholder_id === stakeholder.stakeholder.stakeholder_id);
        const relationships = relatedAccounts.map((a): StakeholderRelationship => ({
          relationship_id: uuidv4(),
          stakeholder_id: stakeholder.stakeholder.stakeholder_id,
          entity_type: EntityType.LOAN_ACCOUNT,
          entity_id: a.account_number,
          related_as: RelatedAs.PRIMARY_BORROWER
        }))
        return {
          ...stakeholder, 
          relationships: relationships,
          isNew: true
        }
      });

    return [...existingOptions, ...newOptions];
  }, [existingStakeholders, newStakeholders, newCreditAccounts]);

  const addAccount = () => {
    setNewCreditAccounts(prev => [...prev, { account_number: uuidv4() }]);
  }

  const products = useAppSelector(getProducts);
  const productOptions = products.map(product => ({
    name: product,
    value: product as CreditAccountProduct,
  }));

  

  const [showErrors, setShowErrors] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);

  const accountErrors = (account: CreateCreditAccountDataWithId): Record<string, string> => {

    const trimmedNumber = account.ext_account_number?.trim().toLowerCase();
    const errors: Record<string, string> = {};
    const existing = existingAccounts.find((a) => a.ext_account_number?.trim().toLowerCase() === trimmedNumber);

    if (!account.ext_account_number?.trim()) {
      errors.extAccountNumber = "Account number is required.";
    } else if (existing) {
      const facility = existing.credit_facility ? CREDIT_FACILITY_LABELS[existing.credit_facility] : "";
      const details = `${existing.account_name ? "\n• Account Name: " + existing.account_name : ""}${facility ? "\n• Facility: " + facility : ""}\n• Product: ${existing.product}`;

      errors.extAccountNumber = `${account.ext_account_number?.trim()} already exists in this case:${details}`;

    } else if (newCreditAccounts.filter((a) => a.ext_account_number?.trim().toLowerCase() === trimmedNumber).length > 1) {
      errors.extAccountNumber = "Account number must be unique.";
    }
    if (!account.product) errors.product = "Product is required.";
    if (!account.credit_facility) errors.creditFacility = "Credit facility is required.";
    if (isRevampEnabled) {
      if (!account.primary_borrower_stakeholder_id) errors.accountName = "Please select an existing stakeholder or click + Add to create a new one.";
    } else {
      if (!account.account_name?.trim()) errors.accountName = "Account name is required.";
    }

    if (!isRevampEnabled) {
      delete errors.creditFacility;
      delete errors.accountName;
    }

    return errors;
  };

  const isComplete = newCreditAccounts.every(row => Object.keys(accountErrors(row)).length === 0);

  const rowCount = useRef(newCreditAccounts.length);

  useEffect(() => {
    const list = listRef.current;
    if (list && newCreditAccounts.length > rowCount.current) {
      list.scrollTop = list.scrollHeight;
    }
    rowCount.current = newCreditAccounts.length;
  }, [newCreditAccounts.length]);

  const updateAccount = (accountNumber: string, patch: Partial<CreateCreditAccountData>) => {
    if(patch.primary_borrower_stakeholder_id && !patch.account_name) {
      patch.account_name =  stakeholderOptions.find((option) => option.stakeholder.stakeholder_id === patch.primary_borrower_stakeholder_id)?.stakeholder.name
    }
    setNewCreditAccounts((prev) =>
      prev.map((row) => (row.account_number === accountNumber ? { ...row, ...patch } : row)),
    );
  };

  const addStakeholder = (name: string, accountNumber: string) => {
    const stakeholder_id = uuidv4();
    const newStakeholder: FullStakeholderData = {
      stakeholder: {stakeholder_id: stakeholder_id, name: name},
      enriched_stakeholder: {stakeholder_id: stakeholder_id},
      relationships: []
    }
    setNewStakaholders((prev) => ([...prev, newStakeholder]));
    updateAccount(accountNumber, {primary_borrower_stakeholder_id: stakeholder_id, account_name: name})
  }

  const deleteAccount = (accountNumber: string) =>
    setNewCreditAccounts((prev) => prev.filter((row) => row.account_number !== accountNumber));
  
  const handleDiscard = () => {
    setNewCreditAccounts(initialRows());
    onClose();
  };

  const handleSave = async () => {
    setShowErrors(true);
    if (!caseId || !isComplete) {
      return;
    }
    setIsSaving(true);

    const stakeholders: Stakeholder[] = newStakeholders
      .filter(s => newCreditAccounts.some(a => a.primary_borrower_stakeholder_id === s.stakeholder.stakeholder_id)).map(s => s.stakeholder);

    try {
      if (isRevampEnabled) {
        await dispatch(
          addCreditAccounts({
            case_id: caseId,
            credit_accounts: newCreditAccounts.map((account) => ({
              account_number: account.account_number,
              ext_account_number: account.ext_account_number?.trim() || "",
              account_name: account.account_name?.trim() || "",
              product: account.product as CreditAccountProduct,
              credit_facility: account.credit_facility as CreditFacility,
              primary_borrower_stakeholder_id: account.primary_borrower_stakeholder_id,
            })),
            stakeholders: stakeholders,
          })
        ).unwrap();
      } else {
        const updatedLoans = [
          ...loanReferences,
          ...newCreditAccounts.map((account) => ({
            loan_reference_id: account.ext_account_number?.trim() || "",
            product: account.product || "",
          })),
        ];
        await dispatch(
          updateCase({
            caseId,
            request: {
              loan_product_info: updatedLoans,
            },
          })
        ).unwrap();
      }

      showSuccessToast("Loan accounts saved successfully.");
      onClose();
    } catch (error) {
      showErrorToast("Failed to save loan accounts. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AddEntitiesModal
      title={title}
      onClose={onClose}
      onBack={onBack}
      onDiscard={handleDiscard}
      onSave={handleSave}
      saveEnabled={newCreditAccounts.length > 0 && !isSaving && isComplete}
      isSaving={isSaving}
      wide
    >
      <div ref={listRef} className="flex max-h-[50vh] flex-col overflow-y-auto px-3 py-1">
        {prefillExisting && existingAccounts.map((account, index) => (
          <AccountRow
            key={account.account_number}
            account={account}
            errors={{}}
            productOptions={productOptions}
            stakeholderOptions={stakeholderOptions}
            showAllFields={isRevampEnabled}
            showLabels={index === 0}
            showErrors={false}
            disabled
            canDelete={false}
            onCreditAccountChange={(_) => {}}
            onAddStakeholder={(_) => ""}
            onDelete={() => {}}
          />
        ))}

        {newCreditAccounts.map((account, index) => (
          <AccountRow
            key={account.account_number}
            account={account}
            errors={accountErrors(account)}
            productOptions={productOptions}
            stakeholderOptions={stakeholderOptions}
            showAllFields={isRevampEnabled}
            showLabels={index === 0}
            showErrors={showErrors}
            disabled={isSaving}
            canDelete={newCreditAccounts.length > 1}
            onCreditAccountChange={(patch) => {updateAccount(account.account_number, patch)}}
            onAddStakeholder={(name) => addStakeholder(name, account.account_number)}
            onDelete={() => deleteAccount(account.account_number)}
          />
        ))}
      </div>

      <AddLoanAccountButton disabled={!isComplete || isSaving} onClick={addAccount} />
    </AddEntitiesModal>
  );
};

export default CreditAccountsModal;
